import React, { useEffect,useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Table, Input, Button, Row, Col, Spinner, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import useFbLeads from "../../Hooks/useFbLeads";
import LeadFilters from "../../components/FbLeadgen/LeadFilters";
import useStages from "../../Hooks/useStages";
import useProjects from "../../Hooks/useProjects";
import useFbForms from "../../Hooks/useFbForms";
import useFbPages from "../../Hooks/useFbPages";
import LeadAssignModal from "../../Modal/Leads/LeadAssignModal";
import LeadImportModal from "../../Modal/Leads/LeadImportModal";
import useLeads from "../../Hooks/useLeads";
import { toast } from "react-toastify";
import { syncLeadsNowApi } from "../../apiServices/leadServices";
import { exportLeadsApi, archiveLeadsBulkApi, unarchiveLeadsBulkApi } from "../../apiServices/genericLeadServices";
import MobileLeadCards from "../../components/FbLeadgen/MobileLeadCards";
import AppLoader from "../../components/Common/AppLoader";
import useUsersWithLeads from "../../Hooks/useUsersWithLeads";
import usePermission from "../../Hooks/usePermission";

const FbLeads = () => {
  const [filters, setFilters] = useState({ search:"", assignedTo:"",source: "all", page:1, limit:20, stage:"", projectId:"", formId:"", pageId:"", sortBy:"createdTime", sortOrder:"desc", isArchived: false });
  const navigate = useNavigate();
  const { syncLeadsNow, syncLoading } = useLeads();
  const [syncing,setSyncing]=useState(false);
  const [refreshKey,setRefreshKey]=useState(0);
  const [importModal, setImportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const { hasPermission } = usePermission();
  const { users=[] } = useUsersWithLeads();
  const { leads, loading, pagination } =  useFbLeads({ ...filters, refreshKey });
  const [filterModal,setFilterModal]=useState(false);
  const [selectedLeads,setSelectedLeads]=useState([]);
  const [assignModal,setAssignModal]=useState(false);
  const { stages=[] } = useStages({
    limit:1000,
  });
  const { projects=[] } = useProjects({
    limit:1000,
  });

  const handleArchiveLeads = async () => {
    if (!selectedLeads || selectedLeads.length === 0) {
      return toast.warning("Please select at least one lead to archive.");
    }
    try {
      setArchiving(true);
      const res = await archiveLeadsBulkApi({ leads: selectedLeads });
      toast.success(res?.message || `${selectedLeads.length} lead(s) archived successfully!`);
      setSelectedLeads([]);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.message || "Failed to archive leads.");
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchiveLeads = async () => {
    if (!selectedLeads || selectedLeads.length === 0) {
      return toast.warning("Please select at least one lead to restore.");
    }
    try {
      setArchiving(true);
      const res = await unarchiveLeadsBulkApi({ leads: selectedLeads });
      toast.success(res?.message || `${selectedLeads.length} lead(s) restored successfully!`);
      setSelectedLeads([]);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.message || "Failed to restore leads.");
    } finally {
      setArchiving(false);
    }
  };

  const handleExportLeads = async () => {
    if (!selectedLeads || selectedLeads.length === 0) {
      return toast.warning("Please select at least one lead to export.");
    }
    try {
      setExporting(true);
      const blob = await exportLeadsApi({ leads: selectedLeads, ...filters });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `leads_export_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${selectedLeads.length} selected lead(s) exported successfully!`);
    } catch (err) {
      toast.error(err?.message || "Failed to export leads.");
    } finally {
      setExporting(false);
    }
  };
  const formatDateTime = (date) => {
    if (!date) return { date: "-", time: "-" };

    const d = new Date(date);

    return {
      date: d.toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
      time: d.toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
  };
  const { forms=[] } = useFbForms({
    limit:1000,
  });
  const [isMobile,setIsMobile]=useState(window.innerWidth<=768);
  // console.log("screen width",window.innerWidth,isMobile);
  useEffect(()=>{
    const handleResize=()=>setIsMobile(window.innerWidth<=768);

    window.addEventListener("resize",handleResize);

    return ()=>window.removeEventListener("resize",handleResize);
  },[]);
  const { pages=[] } = useFbPages();
  // 🔹 HANDLE CHANGE (NO API CALL HERE)
  const handleChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      page: 1,
    }));
  };
  const getLeadDetailsRoute = (lead) => `/lead-details/${lead.source}/${lead._id}`;

  // 🔹 RESET
  const handleReset = () => {
    setFilters(prev => ({
      ...prev,
      search: "",
      page: 1,
      limit: 20,
      stage: "",
      projectId: "",
      assignedTo:"",
      formId: "",
      pageId: "",
      sortBy: "createdTime",
      sortOrder: "desc",
    }));

    setRefreshKey(prev => prev + 1);
  };
  // 🔹 Clear selected leads when pagination or filters change
  useEffect(() => {
    setSelectedLeads([]);
  }, [filters.page, filters.limit, filters.source, filters.search, filters.stage, filters.projectId, filters.assignedTo, filters.formId, filters.pageId]);

  const handleLeadSelect = (lead) => {
    setSelectedLeads((prev) => {
      const exists = prev.some((item) => item.id === lead._id);

      if (exists) {
        return prev.filter((item) => item.id !== lead._id);
      }

      return [
        ...prev,
        {
          id: lead._id,
          source: lead.source,
        },
      ];
    });
  };

  const isAllSelected =
    leads.length > 0 &&
    leads.every((lead) =>
      selectedLeads.some((l) => l.id === lead._id)
    );

  const handleSelectAll = () => {
    if (isAllSelected) {
      const currentIds = new Set(leads.map((l) => l._id));
      setSelectedLeads((prev) => prev.filter((item) => !currentIds.has(item.id)));
    } else {
      setSelectedLeads((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const newSelections = leads
          .filter((l) => !existingIds.has(l._id))
          .map((l) => ({ id: l._id, source: l.source }));
        return [...prev, ...newSelections];
      });
    }
  };


const handleSyncLeads=async()=>{
  if(syncing){
    return toast.warning( "Lead sync already in progress" );
  }
  try{
    setSyncing(true);
    const response= await syncLeadsNowApi();
    toast.success( response?.message || "Lead synced successfully" );
    setRefreshKey(prev=>prev+1);
  }catch(error){
    toast.error( error?.error || "Failed to start lead sync" );
  }finally{
    setSyncing(false);
  }
};
  return (
    <div className="page-content">
      <Breadcrumbs title="Leads" breadcrumbItem="All Leads" />

      {/* 🔥 FILTER BAR */}
    <div className="card p-3 mb-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        {/* LEFT */}
        <div style={{minWidth:"260px",maxWidth:"400px",width:"100%"}}>
          <div className="position-relative">
            <i className="mdi mdi-magnify" style={{ position:"absolute", top:"10px", left:"12px", fontSize:"16px", color:"#74788d", }} />
            <Input placeholder="Search name / email / phone..." value={filters.search} style={{paddingLeft:"35px"}} onChange={(e)=> handleChange("search",e.target.value) } />
          </div>
        </div>
        {/* RIGHT */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {/* Active / Archived View Switcher */}
          <div className="btn-group me-1" role="group">
            <Button
              color={!filters.isArchived ? "primary" : "light"}
              size="sm"
              className="fw-semibold"
              onClick={() => handleChange("isArchived", false)}
            >
              Active Leads <span className={`badge ${!filters.isArchived ? "bg-white text-primary" : "bg-primary text-white"} ms-1`}>{pagination?.activeCount || 0}</span>
            </Button>
            <Button
              color={filters.isArchived ? "warning" : "light"}
              size="sm"
              className="fw-semibold"
              onClick={() => handleChange("isArchived", true)}
            >
              📦 Archived Leads <span className={`badge ${filters.isArchived ? "bg-dark text-warning" : "bg-warning text-dark"} ms-1`}>{pagination?.archivedCount || 0}</span>
            </Button>
          </div>

          <Input type="select" value={filters.limit} style={{width:"85px"}} onChange={(e)=> handleChange("limit",e.target.value) } >
            {[10,20,50,100].map((v)=>(
              <option key={v} value={v}> {v} </option>
            ))}
          </Input>
          <Button color="primary" onClick={()=>setFilterModal(true)} title="Filter Leads">
            <i className="mdi mdi-filter-variant"/>
          </Button>

          {/* SINGLE ACTIONS DROPDOWN */}
          <UncontrolledDropdown>
            <DropdownToggle
              color={selectedLeads.length > 0 ? "primary" : "secondary"}
              outline={selectedLeads.length === 0}
              className="px-3 fw-semibold d-flex align-items-center gap-1"
            >
              <i className="mdi mdi-lightning-bolt me-1" />
              Actions {selectedLeads.length > 0 ? `(${selectedLeads.length})` : ""}
              <i className="mdi mdi-chevron-down ms-1" />
            </DropdownToggle>
            <DropdownMenu end style={{ minWidth: "200px" }}>
              {!filters.isArchived && (
                <DropdownItem
                  disabled={!selectedLeads.length}
                  onClick={() => setAssignModal(true)}
                >
                  <i className="mdi mdi-account-switch me-2 text-success" />
                  Assign Selected {selectedLeads.length > 0 ? `(${selectedLeads.length})` : ""}
                </DropdownItem>
              )}

              {!filters.isArchived ? (
                <DropdownItem
                  disabled={!selectedLeads.length || archiving}
                  onClick={handleArchiveLeads}
                >
                  <i className="mdi mdi-archive-arrow-down me-2 text-danger" />
                  Archive Selected {selectedLeads.length > 0 ? `(${selectedLeads.length})` : ""}
                </DropdownItem>
              ) : (
                <DropdownItem
                  disabled={!selectedLeads.length || archiving}
                  onClick={handleUnarchiveLeads}
                >
                  <i className="mdi mdi-archive-arrow-up me-2 text-success" />
                  Restore Selected {selectedLeads.length > 0 ? `(${selectedLeads.length})` : ""}
                </DropdownItem>
              )}

              <DropdownItem divider />

              {hasPermission("generic_leads.import") && (
                <DropdownItem onClick={() => setImportModal(true)}>
                  <i className="mdi mdi-upload me-2 text-info" />
                  Import Leads
                </DropdownItem>
              )}

              {hasPermission("generic_leads.export") && (
                <DropdownItem disabled={exporting} onClick={handleExportLeads}>
                  <i className="mdi mdi-download me-2 text-primary" />
                  Export Leads {selectedLeads.length > 0 ? `(${selectedLeads.length})` : ""}
                </DropdownItem>
              )}

              {JSON.parse(localStorage.getItem("authUser"))?.response?.role === "super_admin" && !syncing && (
                <>
                  <DropdownItem divider />
                  <DropdownItem onClick={handleSyncLeads}>
                    <i className="mdi mdi-sync me-2 text-warning" />
                    Sync Now
                  </DropdownItem>
                </>
              )}
            </DropdownMenu>
          </UncontrolledDropdown>

          <Button color="light" onClick={handleReset} title="Reset & Refresh">
            <i className="mdi mdi-refresh"/>
          </Button>
        </div>
      </div>
    </div>

      {/* 🔥 TABLE */}
      <div className="card p-3">
        {
          isMobile ? (
            <MobileLeadCards
              leads={leads}
              loading={loading}
              navigate={navigate}
              selectable={true}
              selectedLeadIds={selectedLeads.map((lead) => lead.id)}
              handleLeadSelect={handleLeadSelect}
              showAssignedUser={true}
              showProject={true}
              showCheckbox={true}
            />
          ) : (
            <div className="table-responsive">
              <Table className="table align-middle table-hover" style={{fontSize:"12px"}}>
                <thead className="table-light">
                  <tr>
                    <th className="lead-checkbox-column">
                      <label
                        className={`crm-checkbox ${isAllSelected ? "active" : ""}`}
                        onClick={(e)=>{
                          e.stopPropagation();
                          handleSelectAll();
                        }}
                      >
                        <span />
                      </label>
                    </th>
                    <th>Name</th>
                    <th>Contact</th>
                    {/* <th>Phone</th> */}
                    <th>Project</th>
                    <th>Form</th>
                    <th>Stage</th>
                    <th>Remark</th>
                    <th>Came Date</th>
                    <th>Added Date</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="text-center">
                        <AppLoader  />
                      </td>
                    </tr>
                  ) : leads.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center">
                        No Leads Found
                      </td>
                    </tr>
                  ) : (
                    leads.map((l) => (
                      <tr key={l._id} style={{ cursor:"pointer", }}>
                        <td
                            className="lead-checkbox-column"
                          
                          >
                            <label
                              className={`crm-checkbox ${
                                selectedLeads.some((lead) => lead.id === l._id) ? "active" : ""
                              }`}
                              onClick={(e)=>{
                                e.stopPropagation();
                                handleLeadSelect(l);
                              }}
                            >
                              <span />
                            </label>
                          </td>
                        <td onClick={()=>navigate(getLeadDetailsRoute(l))} >{l.name || "-"}</td>
                        <td onClick={()=>navigate(getLeadDetailsRoute(l))} >{l.email || "-"} <br/> {l.phone || "-"}</td>
                        <td>{l.projectName || "-"}
                          <br/>
                          {
                            l.assignedTo &&
                            l.assignedTo !== "-" && (
                              <>
                                <br/>
                                <small>
                                  <i className="mdi mdi-account-outline me-1 text-primary"></i>

                                  <Link
                                    to={`/user-leads-page/${l.assignedToId}`}
                                    className="text-primary text-decoration-none"
                                    onClick={(e)=>e.stopPropagation()}
                                  >
                                    {l.assignedTo}
                                  </Link>
                                </small>
                              </>
                            )
                          }
                        </td>
                        <td onClick={() => navigate(getLeadDetailsRoute(l))}>
                          {l.source === "google" ? (
                            <>
                              <div className="d-flex align-items-center gap-1 fw-semibold text-danger">
                                <i className="mdi mdi-google" style={{ fontSize: "15px" }} />
                                Google
                              </div>
                            </>
                          ) : l.source === "facebook" ? (
                            <>
                              <div className="d-flex align-items-center gap-1 fw-semibold text-info mb-1">
                                <i className="mdi mdi-facebook" style={{ fontSize: "15px" }} />
                                Facebook
                              </div>

                              <div>{l.formName || "-"}</div>

                              <span className="badge bg-primary mt-1">
                                {l.pageName || "-"}
                              </span>
                            </>
                          ) : (
                            <>
                              <div className="d-flex align-items-center gap-1 fw-semibold text-success mb-1">
                                <i className="mdi mdi-account-plus-outline" style={{ fontSize: "15px" }} />
                                {l.source === "generic" ? "Generic Lead" : String(l.source).toUpperCase()}
                              </div>
                              <span className="badge bg-soft-info text-info mt-1">
                                {l.formName && l.formName !== "-" ? l.formName : "Manual / Import"}
                              </span>
                            </>
                          )}
                        </td>
                        <td onClick={()=>navigate(getLeadDetailsRoute(l))} > <span className="badge bg-primary"> {l.stage} </span> </td>
                        <td onClick={()=>navigate(getLeadDetailsRoute(l))} > <span className="badge bg-warning text-dark"> {l.remark || "-"} </span> </td>
                        <td onClick={() => navigate(getLeadDetailsRoute(l))} style={{ minWidth: "140px" }} > {l.createdTime ? (() => { const dt = formatDateTime(l.createdTime); return ( <> <div className="fw-semibold">{dt.date}</div> <small className="text-muted"> <i className="mdi mdi-clock-outline me-1" /> {dt.time} </small> </> ); })() : "-"} </td>
                        <td onClick={() => navigate(getLeadDetailsRoute(l))} style={{ minWidth: "140px" }} > {l.dbCreateTime ? (() => { const dt = formatDateTime(l.dbCreateTime); return ( <> <div className="fw-semibold">{dt.date}</div> <small className="text-muted"> <i className="mdi mdi-clock-outline me-1" /> {dt.time} </small> </> ); })() : "-"} </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )
        }
        <LeadFilters
          isOpen={filterModal}
          toggle={()=>setFilterModal(false)}
          filters={filters}
          setFilters={setFilters}
          stages={stages}
          projects={projects}
          forms={forms}
          pages={pages}
          users={users}
        />
        {/* 🔥 PAGINATION */}
        <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
          <Button
            size="sm"
            color="light"
            disabled={pagination.page <= 1}
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                page: prev.page - 1,
              }))
            }
          >
            <i className="mdi mdi-chevron-left" />
          </Button>

          <span className="small">
            Page {pagination.page} / {pagination.totalPages}
          </span>

          <Button
            size="sm"
            color="light"
            disabled={
              pagination.page >= pagination.totalPages
            }
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                page: prev.page + 1,
              }))
            }
          >
            <i className="mdi mdi-chevron-right" />
          </Button>
        </div>
      </div>
      <LeadAssignModal
        isOpen={assignModal}
        toggle={()=>setAssignModal(false)}
        leads={selectedLeads}
        onSuccess={()=>{
          setSelectedLeads([]);
          setRefreshKey(prev=>prev+1);
        }}
      />
      <LeadImportModal
        isOpen={importModal}
        toggle={() => setImportModal(false)}
        onSuccess={() => setRefreshKey((prev) => prev + 1)}
      />
    </div>
  );
};

export default FbLeads;